///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Observes user input activity without coupling to individual gesture
/// handlers: capture-phase listeners on the canvas for pointer/wheel/touch,
/// plus window keydown (ignoring typing in form fields). CameraDirector
/// uses this to cancel automated motion the instant the user takes over,
/// and (phase 3) to detect idleness for auto-orbit.
///

export class IdleMonitor {
	private canvas: HTMLCanvasElement
	private lastInputTime = performance.now()
	private inputListeners: Array<() => void> = []

	private readonly notify = () => {
		this.lastInputTime = performance.now()
		this.inputListeners.forEach(listener => listener())
	}

	private readonly onKeyDown = (e: KeyboardEvent) => {
		// Typing in a form field is not camera input
		const target = e.target as HTMLElement | null
		if (target && (
			target.tagName === "INPUT" ||
			target.tagName === "TEXTAREA" ||
			target.tagName === "SELECT" ||
			target.isContentEditable
		)) return
		this.notify()
	}

	constructor(canvas: HTMLCanvasElement) {
		this.canvas = canvas
		canvas.addEventListener("pointerdown", this.notify, { capture: true })
		canvas.addEventListener("wheel", this.notify, { capture: true, passive: true })
		canvas.addEventListener("touchstart", this.notify, { capture: true, passive: true })
		window.addEventListener("keydown", this.onKeyDown, { capture: true })
	}

	public destroy(): void {
		this.canvas.removeEventListener("pointerdown", this.notify, { capture: true })
		this.canvas.removeEventListener("wheel", this.notify, { capture: true })
		this.canvas.removeEventListener("touchstart", this.notify, { capture: true })
		window.removeEventListener("keydown", this.onKeyDown, { capture: true })
		this.inputListeners = []
	}

	/** Register a callback fired on any user input */
	public onInput(listener: () => void): void {
		this.inputListeners.push(listener)
	}

	/**
	 * Count an input that didn't arrive through the canvas (HUD camera
	 * buttons: zoom, tilt, compass, my location). Resets the idle clock
	 * and fires the input listeners like a pointer gesture would.
	 */
	public recordInput(): void {
		this.notify()
	}

	/** Seconds since the last observed user input */
	public get idleSeconds(): number {
		return (performance.now() - this.lastInputTime) / 1000
	}
}
